import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Filter } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader } from "@/components/ui";
import { TextInput, Select } from "@/components/fields";
import { fmtDateTime } from "@/lib/format";
import type { ActivityLogEntry, Paged } from "@shared/types";

export function AdminActivityLog() {
  const [filters, setFilters] = useState({ q: "", action: "", entityType: "", from: "", to: "" });
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const qs = new URLSearchParams({ page: String(page), pageSize: "50" });
  Object.entries(filters).forEach(([k, v]) => v && qs.set(k, v));

  const { data, isLoading } = useQuery({
    queryKey: ["activity-log", filters, page],
    queryFn: () => api.get<Paged<ActivityLogEntry>>(`/activity-log?${qs.toString()}`),
  });
  const { data: actionsData } = useQuery({ queryKey: ["log-actions"], queryFn: () => api.get<{ actions: string[] }>("/activity-log/actions") });

  const pages = data ? Math.ceil(data.total / data.pageSize) : 1;

  return (
    <>
      <PageHeader title="Activity Log" subtitle="Immutable audit trail. Visible to Super Admin and Admin only."
        actions={<a className="btn-outline text-xs" href="/api/activity-log/export"><Download size={14} /> Export CSV</a>} />

      <div className="card mb-3 flex flex-wrap items-end gap-2 p-3">
        <label className="text-xs"><span className="label">Search</span>
          <TextInput className="!w-48" value={filters.q} onChange={(e) => { setFilters({ ...filters, q: e.target.value }); setPage(1); }} placeholder="summary or actor" /></label>
        <label className="text-xs"><span className="label">Action</span>
          <Select className="!w-44" value={filters.action} onChange={(e) => { setFilters({ ...filters, action: e.target.value }); setPage(1); }}>
            <option value="">All</option>
            {(actionsData?.actions ?? []).map((a) => <option key={a} value={a}>{a}</option>)}
          </Select></label>
        <label className="text-xs"><span className="label">From</span><TextInput type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} /></label>
        <label className="text-xs"><span className="label">To</span><TextInput type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} /></label>
        <button className="btn-ghost text-xs" onClick={() => { setFilters({ q: "", action: "", entityType: "", from: "", to: "" }); setPage(1); }}><Filter size={13} /> Clear</button>
      </div>

      {isLoading ? <PageLoader /> : (
        <>
          <div className="card overflow-x-auto p-0">
            <table className="w-full min-w-[760px] text-sm">
              <thead><tr>{["Time", "Actor", "Action", "Entity", "Summary", "IP"].map((h) => <th key={h} className="th">{h}</th>)}</tr></thead>
              <tbody>
                {data?.items.map((e) => (
                  <tr key={e.id} className="border-b border-line align-top hover:bg-surface-2/40 cursor-pointer" onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                    <td className="td whitespace-nowrap text-xs text-ink-faint">{fmtDateTime(e.createdAt)}</td>
                    <td className="td">{e.actorLabel || "—"}</td>
                    <td className="td"><code className="text-xs">{e.action}</code></td>
                    <td className="td text-xs text-ink-faint">{e.entityType}{e.entityId ? ` · ${e.entityId.slice(0, 8)}` : ""}</td>
                    <td className="td">
                      {e.summary}
                      {expanded === e.id && e.meta && (
                        <pre className="mt-1 max-w-xl overflow-x-auto rounded bg-surface-2 p-2 text-[11px]">{JSON.stringify(e.meta, null, 2)}</pre>
                      )}
                    </td>
                    <td className="td text-xs text-ink-faint">{e.ip}</td>
                  </tr>
                ))}
                {!data?.items.length && <tr><td className="td text-ink-faint" colSpan={6}>No log entries match.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center justify-between text-sm">
            <span className="text-ink-faint">{data?.total} entries</span>
            <div className="flex items-center gap-2">
              <button className="btn-outline !py-1 text-xs" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
              <span className="text-xs">Page {page} / {pages}</span>
              <button className="btn-outline !py-1 text-xs" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
